import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ChevronRight, MessageCircle, Truck, Fuel, Cog, Gauge, DoorOpen, Leaf, Loader2 } from "lucide-react";
import { Header } from "@/components/Header";
import { LeadForm } from "@/components/LeadForm";
import { WhatsIncluded } from "@/components/WhatsIncluded";
import { UsatoStrip } from "@/components/UsatoStrip";
import { OffersGrid } from "@/components/OffersGrid";
import { Reviews } from "@/components/Reviews";
import { Faq } from "@/components/Faq";
import { MobileStickyBar } from "@/components/MobileStickyBar";
import { Footer } from "@/components/Footer";
import { Reveal } from "@/components/Reveal";
import { getOfferta } from "@/lib/api";
import { useSeo, faqSchema } from "@/lib/seo";
import { waLink } from "@/data/content";
import { prefillAuto, scrollToId, consumePrefill, PREFILL_EVENT } from "@/lib/smooth";
import { anticipoDefault, anticipoOptions, canoneConAnticipo, conIva, fmtEuro, varianteDefault } from "@/lib/prezzi";

const SpecItem = ({ icon: Icon, label, value }) => (
  <div className="flex items-center gap-3 rounded-xl border border-xmove-ink/8 bg-white px-4 py-3.5">
    <Icon size={18} className="shrink-0 text-xmove-orange" />
    <span>
      <span className="block text-[11px] uppercase tracking-wider text-xmove-muted">{label}</span>
      <span className="block text-sm font-bold text-xmove-ink">{value}</span>
    </span>
  </div>
);

const selectCls =
  "w-full appearance-none rounded-xl border border-xmove-ink/15 bg-xmove-cream px-3.5 py-2.5 text-sm text-xmove-ink outline-none transition-all focus:border-xmove-orange focus:ring-2 focus:ring-xmove-orange/20";

export default function ModelPage() {
  const { marca, modello } = useParams();
  const [offer, setOffer] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [activeImg, setActiveImg] = useState(0);
  const [selDurata, setSelDurata] = useState(null);
  const [selKm, setSelKm] = useState(null);
  const [anticipo, setAnticipo] = useState(null);
  const [privato, setPrivato] = useState(false);
  const [flashForm, setFlashForm] = useState(false);

  const flashPreventivo = () => {
    setFlashForm(true);
    scrollToId("preventivo");
    setTimeout(() => setFlashForm(false), 3400);
  };

  // arrivo da una scheda offerta ("Richiedi questa offerta"): porta al form e lo evidenzia
  useEffect(() => {
    if (offer && window.location.hash === "#preventivo") {
      // se la pagina non si e' rimontata (es. da offerta correlata), il form e' gia' montato: notifica via evento
      const saved = consumePrefill();
      if (saved) window.dispatchEvent(new CustomEvent(PREFILL_EVENT, { detail: saved }));
      const t = setTimeout(flashPreventivo, 250);
      return () => clearTimeout(t);
    }
  }, [offer]);

  useEffect(() => {
    setOffer(null);
    setNotFound(false);
    setActiveImg(0);
    setSelDurata(null);
    setSelKm(null);
    setAnticipo(null);
    setPrivato(false);
    getOfferta(marca, modello)
      .then(setOffer)
      .catch(() => setNotFound(true));
  }, [marca, modello]);

  // ---- logica prezzi ----
  const varianti = offer?.varianti?.length ? offer.varianti : null;
  const varDefault = varianteDefault(varianti);
  const durate = varianti ? [...new Set(varianti.map((v) => v.durata_mesi))].sort((a, b) => a - b) : [];
  const durataEff = selDurata ?? varDefault?.durata_mesi;
  const kmList = varianti
    ? [...new Set(varianti.filter((v) => v.durata_mesi === durataEff).map((v) => v.km_annui))].sort((a, b) => a - b)
    : [];
  const kmEff = kmList.includes(selKm) ? selKm : (varDefault && varDefault.durata_mesi === durataEff ? varDefault.km_annui : kmList[0]);
  const variante = varianti
    ? varianti.find((v) => v.durata_mesi === durataEff && v.km_annui === kmEff) || varDefault
    : null;
  const baseCanone = variante ? variante.canone : offer?.canone;
  const baseDurata = variante ? variante.durata_mesi : offer?.durata_mesi;
  const baseKm = variante ? variante.km_annui : offer?.km_annui;
  const hasCalc = baseCanone != null && !!baseDurata;
  const antOpts = hasCalc ? anticipoOptions(baseCanone, baseDurata) : [];
  const antDef = antOpts.length > 1 ? anticipoDefault(baseCanone, baseDurata) : 0;
  const antEff = hasCalc ? anticipo ?? offer.anticipo ?? antDef : 0;
  // le offerte con anticipo gia' fissato (listino seed) restano a canone pieno
  const canoneCalc = hasCalc
    ? offer.anticipo != null && !variante
      ? baseCanone
      : canoneConAnticipo(baseCanone, baseDurata, antEff)
    : null;
  const canoneShow = canoneCalc != null && privato ? conIva(canoneCalc) : canoneCalc;
  // Priorita' al testo inserito dal pannello (es. "Consegna in 90gg"); altrimenti il flag della variante.
  const consegna = offer?.tempi_consegna?.trim()
    ? offer.tempi_consegna
    : variante
      ? variante.pronta_consegna
        ? "Pronta consegna"
        : "Su ordinazione"
      : null;

  const fullName = offer ? `${offer.marca} ${offer.modello} ${offer.allestimento}` : "";
  const h1 = offer ? offer.h1 || `${offer.marca} ${offer.modello} a Noleggio Lungo Termine` : "";

  useSeo(
    offer
      ? {
          title:
            offer.meta_title ||
            ((canoneShow ?? offer.canone) != null
              ? `${offer.marca} ${offer.modello} Noleggio Lungo Termine: da €${canoneShow ?? offer.canone}/mese | XMOVE`
              : `${offer.marca} ${offer.modello} Noleggio Lungo Termine | XMOVE`),
          description:
            offer.meta_description ||
            (offer.canone != null
              ? `${fullName} a noleggio lungo termine: canone da €${offer.canone}/mese + IVA${[
                  offer.alimentazione?.toLowerCase(),
                  offer.cambio ? `cambio ${offer.cambio.toLowerCase()}` : null,
                ]
                  .filter(Boolean)
                  .join(", ")}. Preventivo gratuito, ti richiamiamo in pochi minuti.`
              : `${fullName} a noleggio lungo termine con XMOVE: canone su misura in base a durata, chilometraggio e anticipo. Preventivo gratuito, ti richiamiamo in pochi minuti.`),
          path: `/offerte/${marca}/${modello}/`,
          jsonLd: [
            {
              "@context": "https://schema.org",
              "@type": "Product",
              name: `${offer.marca} ${offer.modello} — Noleggio Lungo Termine`,
              image: offer.foto || undefined,
              brand: { "@type": "Brand", name: offer.marca },
              offers: {
                "@type": "Offer",
                price: canoneShow ?? offer.canone,
                priceCurrency: "EUR",
                availability: "https://schema.org/InStock",
                url: `https://xmoveitalia.com/offerte/${marca}/${modello}/`,
              },
            },
            offer.faqs ? faqSchema(offer.faqs) : null,
          ].filter(Boolean),
        }
      : { title: "Offerta auto | XMOVE Italia", description: "Noleggio lungo termine XMOVE.", path: `/offerte/${marca}/${modello}/` }
  );

  if (notFound) {
    return (
      <div data-testid="model-not-found">
        <Header />
        <main className="mx-auto max-w-2xl px-5 py-32 text-center">
          <h1 className="font-display text-3xl font-bold text-xmove-ink">Offerta non disponibile</h1>
          <p className="mt-4 text-xmove-muted">Il modello che cerchi non è in listino al momento. Guarda le altre offerte o chiedici un preventivo personalizzato.</p>
          <Link to="/" data-testid="notfound-home-link" className="mt-8 inline-block rounded-xl bg-xmove-orange px-7 py-3.5 text-sm font-bold text-white hover:bg-xmove-orangeDark">
            Torna alle offerte
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  if (!offer) {
    return (
      <div className="flex min-h-screen items-center justify-center" data-testid="model-loading">
        <Loader2 className="animate-spin text-xmove-orange" size={36} />
      </div>
    );
  }

  const gallery = offer.gallery?.length ? offer.gallery : [];
  const waModel = waLink(`Salve XMOVE, sono interessato alla ${fullName} a noleggio lungo termine. Mi fate un preventivo?`);

  const specs = [
    { icon: Fuel, label: "Alimentazione", value: offer.alimentazione },
    { icon: Cog, label: "Cambio", value: offer.cambio },
    { icon: Gauge, label: "Potenza", value: offer.potenza },
    { icon: DoorOpen, label: "Porte", value: offer.porte },
    { icon: Leaf, label: "Emissioni", value: offer.emissioni },
    { icon: Truck, label: "Consegna", value: (consegna || "").split("·")[0].trim() || null },
  ].filter((s) => s.value);

  const descrFallback = `La ${fullName} è disponibile a noleggio lungo termine con XMOVE${[
    offer.alimentazione ? `alimentazione ${offer.alimentazione.toLowerCase()}` : null,
    offer.cambio ? `cambio ${offer.cambio.toLowerCase()}` : null,
    offer.potenza || null,
  ]
    .filter(Boolean)
    .join(", ")
    .replace(/^/, ": ")}. Nel canone mensile è incluso tutto: RCA, Kasko, manutenzione ordinaria e straordinaria, soccorso stradale H24, protezione cristalli, tutela legale, infortunio conducente, protezione da eventi atmosferici e gestione sinistri. I pneumatici non sono inclusi.${
    varianti
      ? " Scegli durata, chilometraggio e anticipo qui accanto: il canone si aggiorna in tempo reale."
      : " Il canone è costruito su misura in base a durata, chilometraggio e anticipo: richiedi il preventivo gratuito e ti richiamiamo in pochi minuti."
  }`;

  const ctaPrefill = variante
    ? `${fullName} — ${baseDurata} mesi, ${baseKm?.toLocaleString("it-IT")} km/anno, anticipo ${antEff === 0 ? "zero" : fmtEuro(antEff)}`
    : fullName;

  return (
    <div className="pb-[72px] md:pb-0" data-testid="model-page">
      <Header />
      <main>
        <nav className="mx-auto flex max-w-7xl items-center gap-1.5 px-5 pt-6 text-xs text-xmove-muted lg:px-8" data-testid="breadcrumb">
          <Link to="/" className="hover:text-xmove-orange">Home</Link>
          <ChevronRight size={13} />
          <Link to="/offerte/" className="hover:text-xmove-orange" data-testid="breadcrumb-offerte">Offerte</Link>
          <ChevronRight size={13} />
          <span className="font-semibold text-xmove-ink">{offer.marca} {offer.modello}</span>
        </nav>

        <section className="mx-auto grid max-w-7xl items-start gap-10 px-5 py-8 lg:grid-cols-[1.25fr_1fr] lg:px-8 lg:py-12">
          <div>
            <Reveal>
              <div className="overflow-hidden rounded-2xl shadow-lift">
                {gallery.length ? (
                  <img
                    src={gallery[activeImg]}
                    alt={`${fullName} a noleggio lungo termine`}
                    decoding="async"
                    className="aspect-[16/10] w-full object-cover"
                    data-testid="model-main-photo"
                  />
                ) : (
                  <div className="flex aspect-[16/10] w-full flex-col items-center justify-center gap-3 bg-xmove-cream" data-testid="model-main-photo">
                    <img src="/images/logo.png" alt="" aria-hidden="true" className="h-16 w-16 opacity-30" />
                    <span className="text-xs font-semibold uppercase tracking-widest text-xmove-muted">Foto in arrivo</span>
                  </div>
                )}
              </div>
            </Reveal>
            {gallery.length > 1 && (
              <div className="mt-4 grid grid-cols-3 gap-3" data-testid="model-gallery">
                {gallery.map((g, i) => (
                  <button
                    key={i}
                    onClick={() => setActiveImg(i)}
                    data-testid={`model-thumb-${i}`}
                    className={`overflow-hidden rounded-xl transition-all ${activeImg === i ? "ring-2 ring-xmove-orange" : "opacity-70 hover:opacity-100"}`}
                  >
                    <img src={g} alt={`${fullName} — foto ${i + 1}`} loading="lazy" className="aspect-[16/10] w-full object-cover" />
                  </button>
                ))}
              </div>
            )}

            <Reveal delay={0.1}>
              <div className="mt-10 grid grid-cols-2 gap-3 sm:grid-cols-3" data-testid="model-specs">
                {specs.map((s) => (
                  <SpecItem key={s.label} icon={s.icon} label={s.label} value={s.value} />
                ))}
              </div>
            </Reveal>

            <Reveal delay={0.15}>
              <div className="mt-12 max-w-3xl" data-testid="model-description">
                <h2 className="font-display text-2xl font-bold text-xmove-ink sm:text-3xl">
                  Perché scegliere la {offer.marca} {offer.modello} a noleggio
                </h2>
                <p className="mt-5 text-base leading-relaxed text-xmove-muted">{offer.descrizione || descrFallback}</p>
                {(offer.anticipo == null || variante) && (
                  <>
                    <h3 className="mt-8 font-display text-xl font-bold text-xmove-ink">Anticipo flessibile, canone su misura</h3>
                    <p className="mt-3 text-base leading-relaxed text-xmove-muted">
                      L'anticipo indicato è una base di partenza: puoi ridurlo o alzarlo a passi di 1.000 euro
                      e il canone mensile si ricalcola subito, in modo matematico e trasparente. Durata e chilometraggio
                      si scelgono tra le formule disponibili: il preventivo XMOVE è costruito sul tuo utilizzo reale,
                      non su un listino rigido. Per partite IVA e aziende prepariamo anche la simulazione di
                      deducibilità e detraibilità IVA da consegnare al commercialista.
                    </p>
                  </>
                )}
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.1} className="lg:sticky lg:top-24">
            <div className="rounded-2xl border border-xmove-ink/8 bg-white p-6 shadow-lift sm:p-7" data-testid="model-rate-card">
              {offer.badge && (
                <span className="inline-block rounded-full bg-xmove-orange/12 px-3.5 py-1.5 text-[11px] font-bold uppercase tracking-wider text-xmove-orange">
                  {offer.badge}
                </span>
              )}
              <h1 className="mt-3 font-display text-3xl font-bold leading-tight text-xmove-ink" data-testid="model-h1">
                {h1}
              </h1>
              <p className="mt-1 text-sm text-xmove-muted">{fullName}</p>

              {canoneShow != null && (
                <div className="mt-4 flex rounded-xl border border-xmove-ink/10 bg-xmove-cream p-1" data-testid="model-iva-toggle">
                  <button
                    onClick={() => setPrivato(false)}
                    data-testid="model-iva-piva"
                    className={`flex-1 rounded-lg py-2 text-xs font-bold transition-colors ${!privato ? "bg-xmove-night text-white" : "text-xmove-muted hover:text-xmove-ink"}`}
                  >
                    P.IVA / Aziende
                  </button>
                  <button
                    onClick={() => setPrivato(true)}
                    data-testid="model-iva-privato"
                    className={`flex-1 rounded-lg py-2 text-xs font-bold transition-colors ${privato ? "bg-xmove-night text-white" : "text-xmove-muted hover:text-xmove-ink"}`}
                  >
                    Privati · IVA incl.
                  </button>
                </div>
              )}

              <div className="mt-5 flex items-baseline gap-2">
                {canoneShow != null ? (
                  <>
                    {varianti && varianti.length > 1 && <span className="text-sm text-xmove-muted">da</span>}
                    <span className="font-display text-5xl font-bold text-xmove-orange" data-testid="model-canone">€ {canoneShow}</span>
                    <span className="text-sm text-xmove-muted">{privato ? "/mese IVA inclusa" : "/mese + IVA"}</span>
                  </>
                ) : (
                  <span className="font-display text-2xl font-bold text-xmove-orange" data-testid="model-canone">Canone su richiesta</span>
                )}
              </div>

              {varianti && (
                <div className="mt-5 space-y-3 border-t border-xmove-ink/8 pt-5">
                  <div className="grid grid-cols-2 gap-3">
                    <label className="block">
                      <span className="mb-1.5 block text-[11px] font-bold uppercase tracking-wider text-xmove-muted">Durata</span>
                      <select
                        value={durataEff}
                        onChange={(e) => {
                          setSelDurata(Number(e.target.value));
                          setSelKm(null);
                        }}
                        data-testid="model-durata-select"
                        className={selectCls}
                      >
                        {durate.map((d) => (
                          <option key={d} value={d}>{d} mesi</option>
                        ))}
                      </select>
                    </label>
                    <label className="block">
                      <span className="mb-1.5 block text-[11px] font-bold uppercase tracking-wider text-xmove-muted">Chilometraggio</span>
                      <select
                        value={kmEff}
                        onChange={(e) => setSelKm(Number(e.target.value))}
                        data-testid="model-km-select"
                        className={selectCls}
                      >
                        {kmList.map((k) => (
                          <option key={k} value={k}>{k.toLocaleString("it-IT")} km/anno</option>
                        ))}
                      </select>
                    </label>
                  </div>
                  <label className="block">
                    <span className="mb-1.5 block text-[11px] font-bold uppercase tracking-wider text-xmove-muted">Anticipo: lo scegli tu</span>
                    <select
                      value={antEff}
                      onChange={(e) => setAnticipo(Number(e.target.value))}
                      data-testid="model-anticipo-select"
                      className={selectCls}
                    >
                      {antOpts.map((a) => (
                        <option key={a} value={a}>
                          {a === 0 ? "Zero" : fmtEuro(a)}{a === antDef && a !== 0 ? " (consigliato)" : ""}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
              )}

              <div className="mt-5 space-y-2.5 border-t border-xmove-ink/8 pt-5 text-sm">
                <p className="flex justify-between"><span className="text-xmove-muted">Anticipo</span><b className="text-xmove-ink" data-testid="model-anticipo">{variante ? (antEff === 0 ? "Zero" : fmtEuro(antEff)) : antEff ? fmtEuro(antEff) : "—"}</b></p>
                <p className="flex justify-between"><span className="text-xmove-muted">Durata</span><b className="text-xmove-ink" data-testid="model-durata">{baseDurata ? `${baseDurata} mesi` : "Su misura"}</b></p>
                <p className="flex justify-between"><span className="text-xmove-muted">Km inclusi</span><b className="text-xmove-ink" data-testid="model-km">{baseKm ? `${baseKm.toLocaleString("it-IT")} / anno` : "Su misura"}</b></p>
                <p className="flex justify-between"><span className="text-xmove-muted">Consegna</span><b className="text-right text-xmove-green">{consegna || "Da concordare"}</b></p>
              </div>
              <button
                onClick={() => { prefillAuto(ctaPrefill); flashPreventivo(); }}
                data-testid="model-cta-btn"
                className="mt-6 w-full rounded-xl bg-xmove-orange py-4 text-[15px] font-bold text-white shadow-soft transition-all hover:bg-xmove-orangeDark hover:shadow-lift active:scale-[0.99]"
              >
                Richiedi questa offerta
              </button>
              <a
                href={waModel}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="model-whatsapp-btn"
                className="mt-3 flex w-full items-center justify-center gap-2 rounded-xl border-2 border-xmove-green py-3.5 text-sm font-bold text-xmove-green transition-colors hover:bg-xmove-green hover:text-white"
              >
                <MessageCircle size={16} /> Chiedi su WhatsApp
              </a>
            </div>
          </Reveal>
        </section>

        <WhatsIncluded dark />
        <UsatoStrip
          marca={offer.marca}
          modello={offer.modello}
          limit={3}
          titolo={`${offer.marca} anche in noleggio lungo termine usato (NLT Re-Use)`}
          sottotitolo="Stessa qualità di servizio, auto già immatricolata con massimo 70/80.000 km: risparmio fino al 40% sul canone."
          testid="model-usato-strip"
        />

        <section id="preventivo" className="scroll-mt-24 bg-white py-16 lg:py-24" data-testid="model-form-section">
          <div className="mx-auto grid max-w-7xl items-center gap-10 px-5 lg:grid-cols-2 lg:px-8">
            <Reveal>
              <h2 className="font-display text-3xl font-bold text-xmove-ink sm:text-4xl">
                Preventivo per la {offer.marca} {offer.modello}
              </h2>
              <p className="mt-4 max-w-md text-base leading-relaxed text-xmove-muted">
                Confermiamo il canone migliore disponibile in
                questo momento per la {fullName}, selezionandolo dallo stock più ampio d'Italia.
                Gratuito e senza impegno, ti richiamiamo in pochi minuti.
              </p>
            </Reveal>
            <Reveal delay={0.12}>
              <div
                className={`rounded-2xl border bg-xmove-cream p-6 transition-all duration-500 sm:p-8 ${
                  flashForm
                    ? "border-xmove-orange shadow-lift ring-4 ring-xmove-orange/40"
                    : "border-xmove-ink/8 shadow-soft"
                }`}
                data-testid="model-lead-form"
              >
                <LeadForm idPrefix="model" compact />
              </div>
            </Reveal>
          </div>
        </section>

        <div className="bg-xmove-cream">
          <OffersGrid
            exclude={`${marca}/${modello}`}
            title="Potrebbero interessarti anche"
            subtitle="Offerte con un canone simile, con gli stessi servizi tutto incluso."
            testId="related-offers"
            limit={4}
            ivaInclusa={privato}
            similarTo={offer.canone}
          />
        </div>

        <Reviews limit={3} />
        {offer.faqs && <Faq faqs={offer.faqs} title={`FAQ — ${offer.marca} ${offer.modello} a noleggio`} />}
      </main>
      <Footer />
      <MobileStickyBar waText={waModel} />
    </div>
  );
}
